import { DateTime } from "luxon";

/*
 * Util | Time Inputs
 */

const whiteSpaceRegex = /\s/;
const whiteSpaceRegexGlobal = /\s/g;

export const stripSpaces = (s: string): string => {
  return s.replaceAll(whiteSpaceRegexGlobal, "");
};

export const convertDTToString = (dtObj: DateTime): string => {
  return stripSpaces(dtObj.toFormat("t"));
};

export const hasWhiteSpace = (s: string): boolean => {
  return whiteSpaceRegex.test(s);
};

/*
 * Regex that accepts either a hh:mmTT or hh:mm TT, TT being the meridiem (am/pm).
 *
 * Both inputs with and without a leading hour zero are okay.
 */
const DTWithMeridiemRegex = /(([0-9]|0[0-9]|1[0-9]|2[0-3]):([0-5][0-9])\s?([AaPp][Mm])?)/;

export const separateTimeAndMeridiem = (timeStr: string): string => {
  const isValidDTString = DTWithMeridiemRegex.test(timeStr);
  if (!isValidDTString) {
    // This code should never be reached without a prior check, so throw here.
    throw new Error("separateTimeAndMeridiem is being called with an invalid time.");
  }
  // Don't do anything if user inputs a AM/PM separated string
  if (hasWhiteSpace(timeStr)) return timeStr;
  // Add space in front of the second to last character
  return timeStr.replace(/.{2}$/, " $&");
};

/*
 * Takes in a time string in the format hh:mmTT or hh:mm TT, and a DateTime Object.
 *
 * The time string is first validated to ensure it has the correct format, then a
 * whitespace is added if it doesn't exist in front of the meridiem (AM/PM) since Luxon
 * requires this whitespace.
 *
 * The DateTime object's hour and minute are then updated to use these values.
 */
export const convertStringToDT = (timeStr: string, dtObj: DateTime): DateTime | undefined => {
  const isValidDTString = DTWithMeridiemRegex.test(timeStr);
  if (!isValidDTString) return;
  const validatedDT = DateTime.fromFormat(separateTimeAndMeridiem(timeStr), "t");
  if (!validatedDT.isValid) return;
  return dtObj.set({ hour: validatedDT.hour, minute: validatedDT.minute });
};

/*
 * Determines whether an "extreme time" label should be shown for a given
 * start and end time.
 *
 * The DateTime objects' date components are ignored entirely. Only the
 * hour and minute fields are used, which are converted into minutes since
 * midnight for easier comparison.
 *
 * The label is shown if ANY of the following conditions are met:
 *
 *  - The start time occurs before 6:00 AM or at/after 5:30 PM
 *  - The end time occurs before 10:00 AM or at/after 6:30 PM
 *  - The duration between the start and end times is <= 2 hours
 *  - The duration between the start and end times is >= 10 hours
 *
 * If the end time occurs earlier in the day than the start time, the
 * function assumes the event crosses midnight and adjusts the duration
 * calculation accordingly.
 *
 * Returns a boolean indicating whether the extreme time label should
 * be displayed.
 */
export function shouldShowExtremeTimeLabel(startTime: DateTime, endTime: DateTime) {
  if (!startTime || !endTime) return false;

  const minutesOfDay = (dt: DateTime) => dt.hour * 60 + dt.minute;

  const startM = minutesOfDay(startTime);
  const endM = minutesOfDay(endTime);

  const startExtreme = startM < 6 * 60 || startM >= 17 * 60 + 30;

  const endExtreme = endM < 10 * 60 || endM >= 18 * 60 + 30;

  let durationMinutes = endM - startM;
  if (durationMinutes < 0) durationMinutes += 24 * 60;

  const durationHours = durationMinutes / 60;
  const shortDuration = durationHours <= 2;
  const longDuration = durationHours >= 10;

  return startExtreme || endExtreme || shortDuration || longDuration;
}
